import FollowButton from "@/components/FollowButton";
import { FollowerDialog } from "@/components/FollowerModal";
import Image from "@/components/Image";
import useAppStore from "@/context/store";
import useRequest from "@/utils/useReq";
import { Popover } from "antd";
import type { TooltipPlacement } from "antd/es/tooltip";
import classNames from "classnames";
import Link from "next/link";
import React, { useCallback, useMemo, useState } from "react";
import { Else, If, Then } from "react-if";
import { followUser, getUser, unfollowUser } from "../../../services";
import displayNum from "../../../utils/displayNum";
import HBAvatar from "../../Avatar";
import { useLogin } from "../../LoginModal";
import { MuseTag } from "../../Muse";
import styles from "./index.module.less";

interface UserCardProps {
  className?: string;
  record: IUser & { following: boolean; custom_recommend?: boolean };
  style?: React.CSSProperties;
  hideButton?: boolean;
  onFollow?: (following: boolean, user: any) => void;
  moduleName?: string;
}

function UserCard({
  className,
  record,
  style,
  hideButton,
  onFollow,
  moduleName,
}: UserCardProps) {
  const user = useAppStore((s) => s.user);
  const { login } = useLogin();
  const { config } = useAppStore(({ config }) => ({ config }));
  const [following, setFollowing] = useState(record?.following);
  const { runAsync: runFollow } = useRequest(followUser, {
    manual: true,
  });
  const { runAsync: runUnfollow } = useRequest(unfollowUser, {
    manual: true,
  });

  const { data: userInfo } = useRequest(async () => {
    const res = await getUser({ urlname: record?.urlname });
    return res?.user;
  });

  const onToggleFollow = useCallback(
    async (e) => {
      // e.stopPropagation();
      e.preventDefault();
      if (!user) {
        const result = await login({
          source_location: "用户关注",
        });
        if (!result) {
          return;
        }
      }

      if (following) {
        const result = await runUnfollow(record?.urlname);
        if (!result.err) {
          record.following = false;
          setFollowing(false);
          onFollow &&
            onFollow(false, {
              ...record,
              following: false,
            });
        }
      } else {
        const result = await runFollow(record?.urlname);
        if (!result.err) {
          record.following = true;
          setFollowing(true);
          onFollow &&
            onFollow(true, {
              ...record,
              following: true,
            });
        }
      }
    },
    [following, login, record, runFollow, runUnfollow, onFollow, user]
  );

  const isOfficial = useMemo(() => {
    return config?.officialUsers?.split(",").includes(record?.urlname);
  }, [record, config]);

  const userHomeLink = useMemo(() => {
    return `/user/${record?.urlname}`;
  }, [record]);

  return (
    <div
      className={classNames([styles.card, className])}
      style={style}
      data-dist-code={record?.custom_recommend ? "人工" : "算法"}
      data-module-name={moduleName}
      data-content-source={moduleName}
    >
      <div className={styles.userAvatar}>
        <Link href={`/user/${record?.urlname}`}>
          <a
            target="_blank"
            data-gd-expose="people_expose"
            data-gd-click="people_click"
            data-people-id={record?.user_id}
          >
            <HBAvatar
              user={record}
              size={62}
              srcSize={180}
              shape="circle"
              withBorder
            />
            <div className={styles.username}>
              <span className={styles.text}>{record?.username}</span>
              {isOfficial && (
                <span className={styles.official} title="花瓣官方"></span>
              )}
              {record?.extra?.is_museuser && (
                <MuseTag
                  user={record}
                  customIcon={
                    <Image
                      src="https://grocery-cdn.huaban.com/file/50506280895b11f0a59ecb9f4a74cfb9.svg"
                      alt="muse"
                      width={20}
                      height={20}
                    />
                  }
                />
              )}
            </div>
          </a>
        </Link>
      </div>
      <div className={styles.info}>
        <Link href={userHomeLink} passHref>
          <a data-dialog-action="点击-画板" data-gd-click="dialog_action">
            <span>{displayNum(userInfo?.board_count)}</span>
            <span>画板</span>
          </a>
        </Link>

        <Link href={`${userHomeLink}/pins`} passHref>
          <a data-dialog-action="点击-采集" data-gd-click="dialog_action">
            <span>{displayNum(userInfo?.pin_count)}</span>
            <span>采集</span>
          </a>
        </Link>

        <a
          onClick={() => {
            FollowerDialog.open({ urlname: userInfo?.urlname });
          }}
          data-dialog-action="点击-粉丝"
          data-gd-click="dialog_action"
        >
          <span>{displayNum(userInfo?.follower_count)}</span>
          <span>粉丝</span>
        </a>
      </div>
      {!hideButton && (
        <div className={styles.followButtonContainer}>
          <If condition={user?.user_id !== record?.user_id}>
            <Then>
              <FollowButton
                className={styles.followButton}
                followed={following}
                userInfo={record}
                onClick={onToggleFollow}
                showGrop
                data-gd-click="button_click"
                data-button-name={following ? "取消关注" : "关注"}
                data-concern-object-type="用户"
                data-concern-object-name={record?.username}
                btnText={following ? "已关注" : "关注"}
              />
            </Then>
            <Else>
              <div className={styles.placeholder}></div>
            </Else>
          </If>
        </div>
      )}
    </div>
  );
}

export default function UserCardPopover({
  record,
  children,
  placement = "bottomLeft" as TooltipPlacement,
  getPopupContainer = (triggerNode) => triggerNode.parentElement,
  moduleName = "",
}) {
  return (
    <Popover
      trigger={"hover"}
      content={<UserCard record={record} moduleName={moduleName} />}
      placement={placement}
      overlayClassName={styles.popover}
      getPopupContainer={getPopupContainer}
    >
      {children}
    </Popover>
  );
}
